<template>
    <NuxtLayout name="main" :dark="true">
        <div :class="css.container" class="container">
            <div :class="css.name" class="text-emphas text-center font-bold">{{ $t("ASOSequencePredictor") }}</div>
            <div :class="css.subjects">
                <!-- * 顶部控制器 -->
                <div :class="css.header" class="text-emphas flex font-bold items-center">
                    <div>{{ useColon($t("ObjectiveTask")) }}</div>
                    <ui-radio-group :class="css.radios" v-model="data.task">
                        <ui-radio value="skip_exon">{{ $t("ExonSkipping") }}</ui-radio>
                        <ui-radio value="include_exon">{{ $t("ExonInclusion") }}</ui-radio>
                    </ui-radio-group>
                </div>

                <!-- * 内容区域 -->
                <div :class="css.content" class="flex">
                    <!-- * 左侧控制器 -->
                    <ui-form :class="css.form" :data="data" :rules="rules" ref="form">
                        <ui-form-item :class="[css.column]" :label="$t('GeneName')" direction="column" prop="id">
                            <default-search
                                v-model="data.name"
                                name="id"
                                type="gene"
                                mode="select"
                                :query="{ tax_names: ['Human'] }"
                                :placeholder="$t('Placeholder.PleaseInputGeneName')"
                                @search="methods.gene.change" />
                        </ui-form-item>
                        <ui-form-item :class="[css.column]" :label="$t('Transcript')" direction="column" prop="transcript">
                            <ui-select
                                v-model="data.transcript"
                                width="100%"
                                :disabled="!usable || !transcripts.length"
                                :candidates="transcripts"
                                @change="methods.getExonRange"
                                :placeholder="$t('Placeholder.PleaseInputTranscript')" />
                        </ui-form-item>

                        <ui-form-item :class="[css.column]" direction="column" prop="exon">
                            <div class="flex items-center mb-[4px]">
                                <div>{{ $t("TargetExon") }}</div>
                                <helper-modal class="ml-[4px]" :size="16" :icon="10" theme="grey"> {{ $t("TargetExonPlaceholder") }} </helper-modal>
                            </div>

                            <div :class="css.inputs">
                                <div :class="css.input">
                                    <div>{{ $t("ExonNo") }}</div>
                                    <div>
                                        <ui-select
                                            v-model="data.exon"
                                            width="100%"
                                            :disabled="!usable || !exons.length"
                                            :candidates="exonCandidates"
                                            :placeholder="$t('Placeholder.PleaseInputExon')"
                                            @change="methods.canvases.render()" />
                                    </div>
                                </div>
                                <div :class="css.input">
                                    <div>
                                        {{ $t("ExonPos") }} <span v-if="min && max">( {{ min }} - {{ max }} )</span>
                                    </div>
                                    <div :class="css.scopes" class="flex items-center">
                                        <ui-input
                                            :class="css.control"
                                            v-model="data.sites.start"
                                            :disabled="!usable"
                                            type="number"
                                            :placeholder="$t('StartingPos')"
                                            @change="methods.sites.start" />
                                        <div :class="css.division"></div>
                                        <ui-input
                                            :class="css.control"
                                            v-model="data.sites.end"
                                            :disabled="!usable"
                                            type="number"
                                            :placeholder="$t('EndingPos')"
                                            @change="methods.sites.end" />
                                    </div>
                                </div>
                            </div>
                        </ui-form-item>

                        <ui-form-item :class="[css.column]" :label="$t('ASOLength')" direction="column">
                            <ui-select v-model="data.length" width="100%" :candidates="lengths" />
                        </ui-form-item>

                        <ui-form-item :class="[css.column]" :label="$t('MutationOPT')" direction="column" ref="mutation">
                            <ui-input
                                :disabled="disableds.mutation"
                                v-model="data.mutation"
                                width="100%"
                                placeholder="e.g.c.467G>A"
                                :maxlength="200"
                                @change="methods.canvases.analysis" />
                        </ui-form-item>

                        <div :class="css.buttons" class="flex">
                            <ui-button :class="[css.button, css_buttons.cancel]" class="flex-1" @click="methods.reset">{{ $t("Reset") }}</ui-button>
                            <ui-button :class="[css.button]" :disabled="disableds.form || !submit" class="flex-1" @click="methods.submit">
                                {{ $t("ASODesign") }}
                            </ui-button>
                        </div>
                    </ui-form>

                    <!-- * 预览区域 -->
                    <div :class="css.view" class="flex-1">
                        <!-- * 无数据占位图 -->
                        <div :class="css.nodata" v-show="disableds.canvas">
                            <div :class="css.title" class="text-emphas font-bold">{{ $t("TargetRegion") }}</div>
                            <div :class="css.content" class="flex flex-col items-center justify-center">
                                <img src="https://rdgta.cdn.cyagen.com/rddc/images/tool/aso/nodata.webp" />
                                <div class="mt-[16px]">{{ $t("TargetRegionPlaceholder") }}</div>
                            </div>
                        </div>

                        <div v-show="!disableds.canvas">
                            <div class="flex items-center justify-between">
                                <div :class="css.title" class="text-emphas font-bold">{{ $t("TargetRegion") }}</div>
                                <div :class="css.legends" class="flex items-center gap-[0_32px]">
                                    <div>
                                        <div :class="[css.legend, css.exon]"></div>
                                        外显子
                                    </div>
                                    <div>
                                        <div :class="[css.legend, css.intron]"></div>
                                        内含子
                                    </div>
                                </div>
                            </div>
                            <div :class="css.name" class="text-emphas font-bold">{{ name }} - {{ data.transcript }}</div>

                            <!-- * 可视化图形 -->
                            <div :class="css.canvas" ref="canvas"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </NuxtLayout>
</template>

<script lang="ts" setup>
//* 组件库
import { UiForm, UiRadio, UiInput, UiSelect, UiButton, UiFormItem, UiRadioGroup } from "various-ui";

//* 公共组件
import { HelperModal } from "@/components/windows";
import { DefaultSearch } from "@/components/controls";
//* 组件属性
import { useComposable } from "./src/composable";
//* 样式
import css from "./index.module.less";
import css_buttons from "@/assets/style/src/button.module.less";

//* 组合函数
const {
    refs: { exons, lengths, disableds, transcripts, name, min, max },
    computeds: { exonCandidates, usable, submit },
    data,
    rules,
    methods,
} = useComposable();
</script>
